import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { MediaCard } from "@/components/MediaCard";
import { MediaLightbox } from "@/components/MediaLightbox";
import { UploadDialog } from "@/components/UploadDialog";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, Play, TrendingUp, Clock, Sparkles, Loader2, MapPin, Bookmark } from "lucide-react";
import { cn } from "@/lib/utils";

const HERO_IMAGE = "https://customer-assets-lqy194kg.emergentagent.net/job_photo-vote-hub-1/artifacts/pb9akg8r_betty.png";

const Home = () => {
  const { user, login, requireAction, requireUpload } = useAuth();
  const navigate = useNavigate();
  const [media, setMedia] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");
  const [typeFilter, setTypeFilter] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (typeFilter !== "all") params.type = typeFilter;
      const [m, f] = await Promise.all([
        api.get("/media", { params }),
        api.get("/media/featured"),
      ]);
      setMedia(m.data);
      setFeatured(f.data);
    } finally {
      setLoading(false);
    }
  }, [sort, typeFilter]);

  useEffect(() => { load(); }, [load]);

  if (user?.is_master_owner) return <Navigate to="/owner" replace />;   // master owner: open BareFootage → already signed in → /owner (phone and desktop)

  return (
    <div className="min-h-screen relative z-10">
      <Navbar onUpload={() => requireUpload() && setUploadOpen(true)} />

      {/* ===== MOBILE HERO: writing on top, picture at the bottom (left-aligned so the FABs never cover it) ===== */}
      <section className="sm:hidden relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative z-10 px-5 pt-8 pb-5 text-left"
        >
          <HeroText featured={featured} navigate={navigate} />
        </motion.div>
        <div className="relative w-full aspect-[4/5] overflow-hidden bg-black">
          {/* blurred fill so the FULL shot shows with no ugly black bars */}
          <img src={HERO_IMAGE} aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 brightness-[0.4]" />
          <img src={HERO_IMAGE} alt="Rank your favourite feet" className="absolute inset-0 w-full h-full object-contain object-center" />
          <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-background to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-background to-transparent" />
        </div>
      </section>

      {/* ===== DESKTOP HERO: cinematic overlay ===== */}
      <section className="hidden sm:flex relative min-h-[calc(100vh-4rem)] items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Rank your favourite feet"
            className="absolute inset-0 w-full h-full object-cover object-center brightness-[0.8]"
          />
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-background/90 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/75 via-background/10 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-l from-black/25 via-transparent to-transparent" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 60% 45%, transparent 55%, rgba(5,5,5,0.4) 100%)" }} />
        </div>
        <div className="relative max-w-[1600px] w-full mx-auto px-8 pt-32 pb-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <HeroText featured={featured} navigate={navigate} />
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-border py-10 text-center text-sm text-muted-foreground">
        <span className="text-primary">BareFootage</span> — upload. vote. rise.
      </footer>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load} />
      <MediaLightbox mediaId={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} />
    </div>
  );
};

const HeroText = ({ featured, navigate }) => (
  <>
    <div className="flex items-center gap-2 text-primary mb-4">
      <TrendingUp className="w-4 h-4" />
      <span className="text-xs tracking-[0.2em] uppercase font-bold">Top Rated Right Now</span>
    </div>
    <h1 className="font-gothic italic text-5xl sm:text-7xl tracking-tight font-medium leading-[0.9]">
      Rank Your<br />Favourite <span className="text-primary not-italic">Feet</span>
    </h1>
    <p className="text-muted-foreground mt-4 text-base max-w-lg">
      Submit your photos. Rate others. Every sole judged by the crowd.
    </p>
    <div className="flex items-center gap-6 mt-6">
      {featured && featured.rating_count > 0 && (
        <div className="flex items-center gap-2">
          <Star className="w-6 h-6 fill-primary text-primary" />
          <span className="font-mono text-2xl font-semibold">{featured.avg_rating}</span>
          <span className="text-muted-foreground text-sm">/10 · {featured.rating_count} votes</span>
        </div>
      )}
      <button
        data-testid="hero-open-btn"
        onClick={() => navigate("/browse")}
        className="flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-medium hover:-translate-y-0.5 transition-transform"
      >
        <Play className="w-4 h-4 fill-current" /> View &amp; Rate
      </button>
    </div>
    <p data-testid="hero-free-note" className="text-xs tracking-wide text-muted-foreground mt-4">
      <span className="text-primary font-semibold">Free to join</span> · 18+ · no payment required — browse & see what's trending for free
    </p>
    <div className="flex flex-wrap gap-2 mt-4">
      {[[Star, "Rate 1–10"], [Sparkles, "AI Search"], [MapPin, "Explore Map"], [Bookmark, "Save Galleries"]].map(([Icon, label]) => (
        <span key={label} className="inline-flex items-center gap-1.5 text-xs rounded-full bg-secondary/50 border border-border px-3 py-1.5 text-foreground/85">
          <Icon className="w-3.5 h-3.5 text-primary" /> {label}
        </span>
      ))}
    </div>
  </>
);

const FilterPills = ({ options, value, onChange, testPrefix }) => (
  <div className="flex items-center gap-1 p-1 rounded-full bg-secondary/40 border border-border">
    {options.map((o) => {
      const Icon = o.icon;
      return (
        <button
          key={o.v}
          data-testid={`${testPrefix}-${o.v}`}
          onClick={() => onChange(o.v)}
          className={cn(
            "flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm transition-colors",
            value === o.v ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {o.l}
        </button>
      );
    })}
  </div>
);

export default Home;
